import {useQuery} from '@github-ui/react-query'
import {makeSelectDataFromQueryData} from '@github-ui/react-core/future/main-query'
import {useRuntimeEnvironment} from '@github-ui/ssr-utils/use-runtime-environment'
import type {CommitsProps} from '@github-ui/commits/shared/Commits'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import {pullRequestsCommitsRoute} from '../../routes/commits-route'
import {usePullRequestBasePath} from '../../stores/layout-selectors'
import type {CommitsRoutePayload} from '../../routes/route-payload-types'

const selectCommits = makeSelectDataFromQueryData<CommitsRoutePayload>()
type DeferredData = NonNullable<CommitsProps['deferredCommitData']>
interface DeferredCommitPage extends DeferredData {
  nextIndex: number
  loadMore: boolean
}

export function useOverviewCommits({enabled}: {enabled: boolean}) {
  const basePath = usePullRequestBasePath()
  const {isServer} = useRuntimeEnvironment()
  const queryKey = pullRequestsCommitsRoute.queries.mainQuery.makeQueryKey({pathname: `${basePath}/commits`})
  return useQuery({
    ...pullRequestsCommitsRoute.buildQueryOptions('mainQuery', queryKey),
    select: selectCommits,
    enabled: !isServer && enabled,
  })
}

// this is split out into its own loader to prevent duplicate data re-loading
async function fetchDeferredCommitData(url: string, signal: AbortSignal): Promise<DeferredData> {
  const deferredCommits: DeferredData['deferredCommits'] = []
  let startIndex = 0

  while (true) {
    const response = await reactFetchJSON(`${url}?start_entry=${startIndex}`, {signal})
    if (!response.ok) throw new Error('Unable to load commit details')
    const page: DeferredCommitPage = await response.json()
    signal.throwIfAborted()
    deferredCommits.push(...page.deferredCommits)
    if (!page.loadMore) return {deferredCommits, renameHistory: page.renameHistory ?? null, loading: false}
    if (page.nextIndex <= startIndex) throw new Error('Commit details pagination did not advance')
    startIndex = page.nextIndex
  }
}

export function useOverviewDeferredCommitDataPaginated({enabled}: {enabled: boolean}): DeferredData {
  const {data, dataUpdatedAt} = useOverviewCommits({enabled})
  const {isServer} = useRuntimeEnvironment()
  const url = data?.metadata.deferredCommitsDataUrl ?? ''
  const query = useQuery({
    queryKey: ['pull-request-overview-deferred-commits', url, dataUpdatedAt],
    queryFn: ({signal}) => fetchDeferredCommitData(url, signal),
    enabled: !isServer && enabled && !!url,
    staleTime: Infinity,
  })

  return {
    deferredCommits: query.data?.deferredCommits ?? [],
    renameHistory: query.data?.renameHistory ?? null,
    loading: query.isPending,
    error: query.isError,
  }
}
